import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useHousehold } from '@contexts/HouseholdContext';
import { streamFlowApi } from '@lib/streamFlowApi';

/**
 * /household — household settings + member roster + pending invites.
 *
 * Members of the household see who else is in it and the pending
 * invite queue. Owner additionally sees:
 *   - "Invite by email" form (creates an invite, surfaces the URL
 *     to copy/share — email delivery is a follow-up)
 *   - "Revoke" button on each pending invite
 *   - "Remove" button on each non-owner member
 *
 * No email-sending — the owner copies the link manually for now.
 * When we wire a transactional email provider (Resend/SES/Postmark),
 * the create endpoint will additionally send the invite to the email
 * address; the URL stays in the response either way.
 */
export default function HouseholdSettings() {
  const { activeHousehold, refresh } = useHousehold();
  const navigate = useNavigate();

  type Member = NonNullable<Awaited<ReturnType<typeof streamFlowApi.listHouseholdMembers>>['data']>[number];
  type Invite = NonNullable<Awaited<ReturnType<typeof streamFlowApi.listHouseholdInvites>>['data']>[number];
  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [submitting, setSubmitting] = useState(false);

  // Primary region (Phase 6d of the taxonomy work). Drives the SPA
  // chip ordering + Tivimate category sort. Saving fires the new
  // PATCH /households/:id endpoint.
  const [primaryRegion, setPrimaryRegion] = useState<string>(
    (activeHousehold as any)?.primary_region ?? 'us',
  );
  const [savingRegion, setSavingRegion] = useState(false);
  // Common-region choices surfaced in the dropdown. Server accepts the
  // full ~50-region list (see VALID_REGIONS in households route); the
  // dropdown is curated for UX.
  const REGION_CHOICES: Array<{ code: string; label: string }> = [
    { code: 'us', label: '🇺🇸 United States' },
    { code: 'uk', label: '🇬🇧 United Kingdom' },
    { code: 'ca', label: '🇨🇦 Canada' },
    { code: 'au', label: '🇦🇺 Australia' },
    { code: 'mx', label: '🇲🇽 Mexico' },
    { code: 'br', label: '🇧🇷 Brazil' },
    { code: 'ar', label: '🇦🇷 Argentina' },
    { code: 'fr', label: '🇫🇷 France' },
    { code: 'de', label: '🇩🇪 Germany' },
    { code: 'es', label: '🇪🇸 Spain' },
    { code: 'it', label: '🇮🇹 Italy' },
    { code: 'pt', label: '🇵🇹 Portugal' },
    { code: 'nl', label: '🇳🇱 Netherlands' },
    { code: 'pl', label: '🇵🇱 Poland' },
    { code: 'ru', label: '🇷🇺 Russia' },
    { code: 'tr', label: '🇹🇷 Turkey' },
    { code: 'sa', label: '🇸🇦 Arabic' },
    { code: 'in', label: '🇮🇳 India' },
    { code: 'cn', label: '🇨🇳 China' },
    { code: 'jp', label: '🇯🇵 Japan' },
    { code: 'kr', label: '🇰🇷 Korea' },
    { code: 'al', label: '🇦🇱 Albania' },
    { code: 'ex-yu', label: 'Ex-Yugoslavia' },
  ];

  const onSaveRegion = async (newRegion: string) => {
    if (!activeHousehold || newRegion === primaryRegion) return;
    setSavingRegion(true);
    const res = await streamFlowApi.setHouseholdPrimaryRegion(activeHousehold.id, newRegion);
    setSavingRegion(false);
    if (res.success) {
      setPrimaryRegion(newRegion);
      toast.success('Primary region updated — refresh Tivimate to see the new order');
      void refresh();
    } else {
      toast.error(res.error?.message ?? 'Failed to update region');
    }
  };

  const reload = async () => {
    if (!activeHousehold) return;
    setLoading(true);
    const [m, i] = await Promise.all([
      streamFlowApi.listHouseholdMembers(activeHousehold.id),
      streamFlowApi.listHouseholdInvites(activeHousehold.id),
    ]);
    if (m.success && m.data) setMembers(m.data);
    if (i.success && i.data) setInvites(i.data);
    setLoading(false);
  };

  useEffect(() => { void reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeHousehold?.id]);

  if (!activeHousehold) {
    return <div className="max-w-2xl mx-auto p-6 text-gray-500">No active household.</div>;
  }

  const isOwner = activeHousehold.role === 'owner';

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setSubmitting(true);
    const res = await streamFlowApi.createHouseholdInvite(activeHousehold.id, inviteEmail.trim(), inviteRole);
    setSubmitting(false);
    if (res.success && res.data) {
      // Drop the URL on the clipboard for instant share.
      try { await navigator.clipboard.writeText(res.data.url); } catch { /* ignore */ }
      toast.success('Invite created — link copied to clipboard');
      setInviteEmail('');
      void reload();
    } else {
      toast.error(res.error?.message ?? 'Failed to create invite');
    }
  };

  const onRevoke = async (inviteId: string) => {
    const res = await streamFlowApi.revokeHouseholdInvite(activeHousehold.id, inviteId);
    if (res.success) {
      toast.success('Invite revoked');
      void reload();
    } else {
      toast.error(res.error?.message ?? 'Failed');
    }
  };

  const onRemoveMember = async (userId: string) => {
    if (!confirm('Remove this member from the household?')) return;
    const res = await streamFlowApi.removeHouseholdMember(activeHousehold.id, userId);
    if (res.success) {
      toast.success('Member removed');
      void reload();
      void refresh();
    } else {
      toast.error(res.error?.message ?? 'Failed');
    }
  };

  const onChangeRole = async (userId: string, role: 'admin' | 'member') => {
    // Optimistic — patch the row immediately, roll back on failure.
    const prev = members;
    setMembers((ms) => ms.map((m) => (m.user_id === userId ? { ...m, role } : m)));
    const res = await streamFlowApi.updateHouseholdMember(activeHousehold.id, userId, { role });
    if (res.success) {
      toast.success('Role updated');
      void reload();
    } else {
      setMembers(prev);
      toast.error(res.error?.message ?? 'Failed to update role');
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{activeHousehold.name}</h1>
          <p className="text-sm text-gray-500">Household settings</p>
        </div>
        <button onClick={() => navigate('/')} className="text-sm text-blue-600 hover:underline">
          ← Dashboard
        </button>
      </div>

      {/* Settings — owner-only edit, all-member visibility */}
      <section className="mb-8">
        <h2 className="font-semibold mb-2">Settings</h2>
        <div className="rounded-lg border border-gray-200 px-3 py-3">
          <label className="block">
            <span className="text-sm font-medium">Primary region</span>
            <p className="text-xs text-gray-500 mt-0.5 mb-2">
              Channels matching this region float to the top of the chip list and Tivimate's category sort. Channels from other regions still appear, just below the divider.
            </p>
            <select
              value={primaryRegion}
              onChange={(e) => onSaveRegion(e.target.value)}
              disabled={!isOwner || savingRegion}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-50"
            >
              {REGION_CHOICES.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
            {!isOwner && <p className="text-xs text-gray-400 mt-1">Only the household owner can change this.</p>}
          </label>
        </div>
      </section>

      {/* Members */}
      <section className="mb-8">
        <h2 className="font-semibold mb-2">Members ({members.length})</h2>
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          {loading && <div className="px-3 py-4 text-sm text-gray-500">Loading…</div>}
          {!loading && members.map((m) => (
            <div key={m.user_id} className="border-t border-gray-100 first:border-0 px-3 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{m.email ?? m.user_id}</div>
                {/* Owner role is fixed (transfer-ownership is a separate flow);
                    non-owner rows get an inline role picker for the owner. */}
                {isOwner && m.role !== 'owner' ? (
                  <select
                    value={m.role}
                    onChange={(e) => onChangeRole(m.user_id, e.target.value as 'admin' | 'member')}
                    className="mt-1 rounded-md border border-gray-300 px-2 py-1 text-xs"
                    aria-label={`Role for ${m.email ?? m.user_id}`}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                ) : (
                  <div className="text-xs text-gray-500 capitalize">{m.role}</div>
                )}
              </div>
              {isOwner && m.role !== 'owner' && (
                <button
                  onClick={() => onRemoveMember(m.user_id)}
                  className="text-xs text-rose-600 hover:underline shrink-0"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Invites — owner-only mutation, all-member visibility */}
      <section className="mb-8">
        <h2 className="font-semibold mb-2">Pending invites ({invites.length})</h2>
        {isOwner && (
          <form onSubmit={onCreate} className="mb-4 flex gap-2">
            <input
              type="email"
              required
              placeholder="email@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
              className="rounded-md border border-gray-300 px-2 py-2 text-sm"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitting ? '…' : 'Invite'}
            </button>
          </form>
        )}

        <div className="rounded-lg border border-gray-200 overflow-hidden">
          {invites.length === 0 && (
            <div className="px-3 py-4 text-center text-sm text-gray-500">No pending invites.</div>
          )}
          {invites.map((inv) => {
            // An invite past its expires_at is dead — the accept flow will
            // reject it server-side. Dim the row and flag it so the owner
            // knows to re-send rather than waiting on a link that won't work.
            const isExpired = new Date(inv.expires_at) < new Date();
            return (
            <div key={inv.id} className={`border-t border-gray-100 first:border-0 px-3 py-3 flex items-center justify-between gap-3 ${isExpired ? 'opacity-50' : ''}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-2">
                  {inv.invited_email}
                  {isExpired && (
                    <span className="shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-rose-100 text-rose-700 border border-rose-200">
                      Expired
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  <span className="capitalize">{inv.role}</span>
                  {' · expires '}{new Date(inv.expires_at).toLocaleDateString()}
                </div>
                {inv.url && (
                  <div className="mt-1 flex items-center gap-2">
                    <code className="text-[10px] text-gray-400 truncate">{inv.url}</code>
                    <button
                      type="button"
                      onClick={async () => { try { await navigator.clipboard.writeText(inv.url!); toast.success('Copied'); } catch {} }}
                      className="text-[10px] text-blue-600 hover:underline shrink-0"
                    >
                      Copy
                    </button>
                  </div>
                )}
              </div>
              {isOwner && (
                <button
                  onClick={() => onRevoke(inv.id)}
                  className="text-xs text-rose-600 hover:underline shrink-0"
                >
                  Revoke
                </button>
              )}
            </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
